const AUTH_KEYS = [
  'access_token',
  'refresh_token',
  'user_info',
  'user_type',
  'login_role',
  'last_shop_name',
];

const inMemoryStorage = new Map();

const browserStorage = typeof window !== 'undefined'
  ? [window.localStorage, window.sessionStorage].filter(Boolean)
  : [];

const canUseStorage = (storage) => {
  try {
    const probeKey = '__sv_auth_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
};

const getPreferredStorage = () => browserStorage.find(canUseStorage) || null;

const getFallbackStorage = () => browserStorage.find((storage) => storage !== getPreferredStorage() && canUseStorage(storage)) || null;

export const getStoredItem = (key) => {
  const preferred = getPreferredStorage();
  if (preferred) {
    const value = preferred.getItem(key);
    if (value !== null) return value;
  }

  const fallback = getFallbackStorage();
  if (fallback) {
    const value = fallback.getItem(key);
    if (value !== null) return value;
  }

  return inMemoryStorage.has(key) ? inMemoryStorage.get(key) : null;
};

export const setStoredItem = (key, value) => {
  const serialized = String(value);
  const preferred = getPreferredStorage();

  if (preferred) {
    preferred.setItem(key, serialized);
    const fallback = getFallbackStorage();
    if (fallback) {
      fallback.removeItem(key);
    }
    inMemoryStorage.delete(key);
    return;
  }

  const fallback = getFallbackStorage();
  if (fallback) {
    fallback.setItem(key, serialized);
    inMemoryStorage.delete(key);
    return;
  }

  inMemoryStorage.set(key, serialized);
};

export const removeStoredItem = (key) => {
  browserStorage.forEach((storage) => {
    try {
      storage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures.
    }
  });
  inMemoryStorage.delete(key);
};

export const clearAuthStorage = () => {
  AUTH_KEYS.forEach(removeStoredItem);
};

export const getStoredAuthSnapshot = () => {
  const token = getStoredItem('access_token');
  const rawUser = getStoredItem('user_info');
  const userType = getStoredItem('user_type');
  const loginRole = getStoredItem('login_role') || userType;

  if (!token || !rawUser) {
    return { token: null, user: null, userType: null, loginRole: null };
  }

  try {
    const user = JSON.parse(rawUser);
    if (!user || typeof user !== 'object') {
      clearAuthStorage();
      return { token: null, user: null, userType: null, loginRole: null };
    }

    return { token, user, userType, loginRole };
  } catch {
    clearAuthStorage();
    return { token: null, user: null, userType: null, loginRole: null };
  }
};